// Inbox items — the renderers for ONE conversation, shared by the Inbox and the
// customer window's Communications tab (ConversationThread). One component per
// channel shape: a text bubble, an email (sandboxed HTML), a call (outcome,
// duration, player, transcript, triage). Helpers for time and channel labels
// live here too so both surfaces format the same way.
import React, { useMemo, useRef, useState } from 'react';
import CallPlayer from '../Phone/CallPlayer';
import CallTranscript from '../Phone/CallTranscript';
import { fetchRecordingUrl } from '../../../api/communications';

export const chLabel = (c) => (c === 'voicemail' ? 'voicemail' : c || 'sms');

export const fmtTime = (v) => {
  if (!v) return '';
  try {
    return new Date(v).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};
export const fmtDay = (v) => {
  if (!v) return '';
  try {
    const d = new Date(v);
    const today = new Date();
    const same = d.toDateString() === today.toDateString();
    return same ? 'Today' : d.toLocaleDateString([], { month: 'short', day: 'numeric', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
  } catch { return ''; }
};
export const fmtRel = (v) => {
  if (!v) return '';
  try {
    const diff = (Date.now() - new Date(v).getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return new Date(v).toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch { return ''; }
};
export const fmtDuration = (s) => {
  const n = Number(s || 0);
  if (!n) return '';
  const m = Math.floor(n / 60);
  return m ? `${m}m ${n % 60}s` : `${n}s`;
};

/* Split a plain-text email body from its quoted history so the thread shows
   the new message, not a wall of forwarded text. */
export const splitQuoted = (body = '') => {
  const text = String(body || '');
  const markers = [/^On .+ wrote:$/m, /^-{2,}\s*Original Message\s*-{2,}$/im, /^_{5,}$/m, /^>{1,}\s?/m];
  let idx = -1;
  for (const re of markers) {
    const m = text.match(re);
    if (m && m.index !== undefined && (idx === -1 || m.index < idx)) idx = m.index;
  }
  if (idx <= 0) return { main: text, quoted: '' };
  return { main: text.slice(0, idx).trim(), quoted: text.slice(idx).trim() };
};

export const stripHtml = (html = '') =>
  String(html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/\s+/g, ' ')
    .trim();

// Human label for the channel key — the tag used to lean on CSS uppercase to hide
// the raw enum; Design Standard v1 dropped uppercase, so the words come from here.
export const CHANNEL_TEXT = { sms: 'SMS', mms: 'MMS', email: 'Email', call: 'Call', voicemail: 'Voicemail', chat: 'Chat' };
export function ChannelTag({ channel }) {
  const c = chLabel(channel);
  const text = CHANNEL_TEXT[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : '');
  return <span className={`ubx-tag ${c === 'voicemail' ? 'voicemail' : c}`}>{text}</span>;
}

/* At-a-glance triage: derive ONE intent per conversation row from its latest
   message. Heuristic, ordered by how much a human needs to see it first —
   automated noise is demoted, opt-out/complaint float to the top. Returns null
   when nothing matches, so the row stays uncluttered rather than mislabeled.
   Reads the backend `intent` field when present (exact for automated), and
   falls back to keyword signal in subject+body for the semantic categories. */
export const INTENT_LABELS = {
  optout: 'Opt-out',
  complaint: 'Complaint',
  quote: 'Quote request',
  document: 'Document',
  scheduling: 'Scheduling',
  automated: 'Automated',
};
export function deriveIntent(row) {
  if (!row) return null;
  const intentField = String(row.intent || '').toLowerCase();
  const text = `${row.subject || ''} ${stripHtml(row.body || '')}`.toLowerCase();

  // Automated: trust the backend intent stamp first, then classic bot markers.
  if (/(^|_)(automated|notification|auto[_-]?reply|system|first[_-]?touch)/.test(intentField)
    || /\b(do[\s-]?not[\s-]?reply|no[\s-]?reply|out of office|automatic reply|delivery (status notification|has failed)|undeliverable|mailer-daemon|this is an automated)\b/.test(text)) {
    return { key: 'automated', label: INTENT_LABELS.automated };
  }
  if (/\b(stop|unsubscribe|opt[\s-]?out|remove me|take me off|do not (contact|text|email|call|message))\b/.test(text)) {
    return { key: 'optout', label: INTENT_LABELS.optout };
  }
  if (/\b(complaint|complain|unhappy|angry|upset|terrible|awful|horrible|worst|refund|disappointed|frustrat\w*|unacceptable|poor service|not happy|never showed|no[\s-]?show)\b/.test(text)) {
    return { key: 'complaint', label: INTENT_LABELS.complaint };
  }
  if (/\b(quote|estimate|pricing|price|how much|what would.*(cost|charge)|cost|charge for|proposal|\bbid\b|ballpark|per (bait )?station)\b/.test(text)) {
    return { key: 'quote', label: INTENT_LABELS.quote };
  }
  if (/\b(invoice|receipt|contract|statement|paperwork|w-?9\b|coi\b|certificate of insurance|copy of|send (me )?(the|my|over))\b/.test(text)) {
    return { key: 'document', label: INTENT_LABELS.document };
  }
  if (/\b(reschedul\w*|reschedule|schedul\w*|appointment|appt|book(ing)?|availab\w*|come out|next (week|month|visit|service)|move (my|the) (appointment|service|visit)|cancel (my|the)? ?(appointment|service|visit)|time slot|what time|push (it|my|the))\b/.test(text)) {
    return { key: 'scheduling', label: INTENT_LABELS.scheduling };
  }
  return null;
}

export function IntentChip({ row }) {
  const intent = deriveIntent(row);
  if (!intent) return null;
  return <span className={`ubx-intent ubx-intent--${intent.key}`}>{intent.label}</span>;
}

/* ---------- message renderers, one per channel shape ---------- */

/* Formatted email body in a sandboxed iframe: no allow-scripts, so nothing in
   the email can execute; allow-same-origin only so we can measure height. */
// Inline attachments (`cid:`) and insecure http:// resources can't load inside the
// sandboxed frame and only produce console errors — drop the former, upgrade the latter.
export const sanitizeEmailHtml = (html = '') =>
  String(html || '')
    .replace(/<img\b[^>]*\bsrc=["']cid:[^"']*["'][^>]*>/gi, '')
    .replace(/\b(src|href)=(["'])http:\/\//gi, '$1=$2https://');

export function HtmlEmailBody({ html }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(80);
  const doc = useMemo(
    () =>
      /* Named colors, not tokens: this styles the email's own document inside
         the sandboxed iframe, where app theme tokens cannot reach. Email HTML
         is authored against a light canvas in both app themes. */
      `<!doctype html><html><head><base target="_blank"><meta http-equiv="Content-Security-Policy" content="upgrade-insecure-requests"><style>
        body{margin:0;padding:2px;font:14px/1.5 -apple-system,system-ui,sans-serif;color:black;background:white;word-break:break-word}
        img{max-width:100%;height:auto}
        blockquote{margin:4px 0 4px 8px;padding-left:8px;border-left:3px solid gainsboro;color:dimgray}
      </style></head><body>${sanitizeEmailHtml(html)}</body></html>`,
    [html]
  );
  const onLoad = () => {
    try {
      const body = ref.current?.contentDocument?.body;
      if (body) setHeight(Math.min(Math.max(body.scrollHeight + 20, 48), 1400));
    } catch { /* sandboxed doc unreadable — keep default height */ }
  };
  return (
    <iframe
      ref={ref}
      className="ubx-email-frame"
      title="Email content"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={doc}
      onLoad={onLoad}
      style={{ height }}
    />
  );
}

export function EmailItem({ msg }) {
  const [showQuoted, setShowQuoted] = useState(false);
  const [showFormatted, setShowFormatted] = useState(true);
  const html = msg.body_html || (/<[a-z][\s\S]*>/i.test(msg.body || '') ? msg.body : '');
  const plain = html ? stripHtml(msg.body_html || msg.body) : (msg.body || '');
  const { main, quoted } = splitQuoted(plain);
  return (
    <div className="ubx-email">
      {msg.subject ? <div className="ubx-email-subject">{msg.subject}</div> : null}
      {html && showFormatted ? (
        <HtmlEmailBody html={html} />
      ) : (
        <div className="ubx-email-body">{main || '(no content)'}</div>
      )}
      {html ? (
        <button className="ubx-quote-toggle" onClick={() => setShowFormatted((v) => !v)}>
          {showFormatted ? 'View as plain text' : 'View formatted'}
        </button>
      ) : null}
      {!(html && showFormatted) && quoted ? (
        <>
          <button className="ubx-quote-toggle" onClick={() => setShowQuoted((v) => !v)}>
            {showQuoted ? 'Hide earlier messages' : 'Show earlier messages'}
          </button>
          {showQuoted ? <div className="ubx-quoted">{quoted}</div> : null}
        </>
      ) : null}
    </div>
  );
}

// A voicemail log's body is the transcript when there is one — and a placeholder when
// there isn't ("Call (inbound)", "(transcription unavailable)", "Voicemail from X — …").
// Only real words render as a transcript; a placeholder is not a transcript.
export const voicemailTranscript = (body = '') => {
  let t = String(body || '').trim();
  t = t.replace(/^voicemail from [^—\n]*—\s*/i, '');
  if (!t || /^\(?transcription unavailable\)?\.?$/i.test(t) || /^call \((inbound|outbound)\)$/i.test(t)) return '';
  return t;
};

export function CallItem({ msg, call, onTriage, onWrapUp }) {
  const isVoicemail =
    chLabel(msg.channel) === 'voicemail' || call?.status === 'voicemail' || Boolean(call?.voicemail);
  const isMissed =
    !isVoicemail && msg.direction !== 'outbound' && !Number(call?.duration_seconds || 0);
  const duration = fmtDuration(call?.duration_seconds);
  const sid = call?.call_sid;
  // Voicemail logs are self-sufficient (audio in media_urls, transcript in
  // body) — older ones carry no call SID, so the Call join may be absent.
  // recording_playback_url is only set on a detail read (or by the API before
  // has_recording existed); a raw recording_url / S3 key is never playable here.
  const recording =
    call?.recording_playback_url || (isVoicemail ? (msg.media_urls || [])[0] : undefined);
  // A list row says has_recording and carries no URL (lane C: the list never
  // presigns); the player fetches GET /comms/calls/<sid>/recording-url/ on Play.
  const fetchOnPlay = !recording && Boolean(call?.has_recording) && Boolean(sid);
  const transcript = call?.transcript || (isVoicemail ? voicemailTranscript(msg.body) : '');
  // One label per fact: the channel tag above the card already says Voicemail /
  // Call, so the card's own title is the OUTCOME — never the channel a second time.
  const title = isVoicemail ? null : isMissed ? 'Missed call' : `${msg.direction === 'outbound' ? 'Outgoing' : 'Incoming'} call`;
  const label = isVoicemail ? 'voicemail' : 'recording';
  return (
    <div className="ubx-call">
      {title || duration ? (
        <div className="ubx-call-top">
          {title ? <strong>{title}</strong> : null}
          {duration ? <span className="ubx-call-dur">{duration}</span> : null}
        </div>
      ) : null}
      {recording ? <CallPlayer key={sid || recording} src={recording} label={label} />
        : fetchOnPlay ? <CallPlayer key={sid} getSrc={() => fetchRecordingUrl(sid)} label={label} />
        : null}
      {call?.ai_summary ? <div className="ubx-ai-summary">{call.ai_summary}</div> : null}
      {transcript ? <CallTranscript text={transcript} className="ubx-transcript" /> : null}
      {!recording && !fetchOnPlay && !transcript ? <span className="ubx-call-dur">No recording captured.</span> : null}
      {sid ? (
        <div className="ubx-call-actions">
          {isVoicemail ? (
            call.handled_at ? (
              <span className="ubx-handled">
                Handled ✓{call.handled_by_name ? ` by ${call.handled_by_name}` : ''}
              </span>
            ) : (
              <button className="ubx-btn sm" onClick={() => onTriage(sid, { handled: true })}>
                Mark handled
              </button>
            )
          ) : null}
          {isMissed ? (
            call.followup_task_id ? (
              <span className="ubx-handled">Follow-up task created</span>
            ) : (
              <button className="ubx-btn sm" onClick={() => onTriage(sid, { create_followup: true })}>
                Create follow-up task
              </button>
            )
          ) : null}
          <button className="ubx-btn sm" onClick={() => onWrapUp(call)}>Wrap-up</button>
        </div>
      ) : null}
    </div>
  );
}

/* Mailgun engagement on an outbound email — the provider's events, not a guess.
   (comm_app/engagement.py → message.engagement) */
export function EngagementChips({ e }) {
  if (!e) return null;
  const t = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
  const chips = [];
  if (e.bounced_at) chips.push(['bounced', 'var(--color-danger)', t(e.bounced_at)]);
  if (e.complained_at) chips.push(['complained', 'var(--color-danger)', t(e.complained_at)]);
  if (e.unsubscribed_at) chips.push(['unsubscribed', 'var(--color-danger)', t(e.unsubscribed_at)]);
  if (e.delivered_at && !e.bounced_at) chips.push(['delivered', 'var(--text-3)', t(e.delivered_at)]);
  if (e.opens) chips.push([`opened${e.opens > 1 ? ` ×${e.opens}` : ''}`, 'var(--accent)', t(e.opened_at)]);
  if (e.clicks) chips.push([`clicked${e.clicks > 1 ? ` ×${e.clicks}` : ''}`, 'var(--accent)', e.last_click_url || t(e.clicked_at)]);
  if (!chips.length && e.deferrals) chips.push([`deferred ×${e.deferrals}`, 'var(--dash-warn)', '']);
  return chips.map(([label, color, title]) => (
    <span key={label} title={title} style={{ color, fontSize: '0.72rem' }}>{label}</span>
  ));
}

export function MessageItem({ msg, call, onTriage, onWrapUp }) {
  const out = msg.direction === 'outbound';
  const channel = chLabel(msg.channel);
  const isCall = channel === 'call' || channel === 'voicemail';
  return (
    <div className={`ubx-msg ${out ? 'out' : 'in'} ${isCall ? 'has-call' : ''}`}>
      <div className="ubx-msg-meta">
        <ChannelTag channel={msg.channel} />
        <span>{fmtTime(msg.timestamp || msg.created_at)}</span>
        {msg.status === 'failed' ? <span style={{ color: 'var(--color-danger)' }}>failed</span> : null}
        <EngagementChips e={msg.engagement} />
      </div>
      {isCall ? (
        <CallItem msg={msg} call={call} onTriage={onTriage} onWrapUp={onWrapUp} />
      ) : (
        <div className="ubx-bubble">
          {channel === 'email' ? <EmailItem msg={msg} /> : (msg.body || '')}
        </div>
      )}
    </div>
  );
}


/* C's data defect: a call log row with no duration, no recording, no transcript
   and no voicemail ("+1610… → Unknown · completed · 0s"). Not a conversation
   event anyone needs to read; the thread folds them behind "Show N system rows". */
export const isGhostCall = (msg, call) => {
  const c = chLabel(msg?.channel);
  if (c !== 'call') return false;
  if (call?.voicemail || call?.status === 'voicemail') return false;
  const secs = Number(call?.duration_seconds || 0);
  const hasMedia = Boolean(call?.has_recording || call?.recording_playback_url || call?.transcript);
  return !secs && !hasMedia;
};

